import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, MaskText, Stagger, StaggerItem } from "./Reveal";
import { STAGES as QUIZ_STAGES } from "./stages";

// Philosophy graphic adds "Birth" between Conception and Infancy.
const BIRTH = { key: "birth", label: "Birth", short: "The idea becomes real. The first customer is asked to buy." };
const STAGES = [QUIZ_STAGES[0], BIRTH, ...QUIZ_STAGES.slice(1)].map((s, i) => ({ ...s, n: String(i + 1).padStart(2, "0") }));

const W = 1200;
const H = 580;
// Organic rising curve through the stage nodes (Catmull-Rom → cubic Bézier).
const POINTS = [
  [90, 440], [236, 426], [382, 396], [527, 352], [673, 302], [819, 256], [964, 214], [1110, 180],
];
const smoothPath = (pts) => {
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${p2[0]} ${p2[1]}`;
  }
  return d;
};
const PATH = smoothPath(POINTS);

const Curve = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 85%", "end 45%"] });
  const len = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <div ref={ref} data-testid="lifecycle-curve" className="hidden md:block relative mt-16">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full overflow-visible" fill="none">
        <path d={PATH} stroke="var(--line)" strokeWidth="1.5" />
        <motion.path d={PATH} stroke="var(--cognac)" strokeWidth="2" style={{ pathLength: len }} />
        {POINTS.map(([x, y], i) => {
          const s = STAGES[i];
          const above = i % 2 === 0;
          return (
            <g key={s.key}>
              <circle cx={x} cy={y} r="14" fill="var(--cream)" stroke="var(--cognac)" strokeWidth="1.5" />
              <circle cx={x} cy={y} r="4" fill="var(--cognac)" />
              <text x={x} y={above ? y - 50 : y + 42} textAnchor="middle" className="font-serif" fill="var(--ocean)" fontSize="44" opacity="0.16">
                {s.n}
              </text>
              <text x={x} y={above ? y - 30 : y + 64} textAnchor="middle" className="font-sans" fill="var(--cognac)" fontSize="11.5" letterSpacing="2.8" style={{ textTransform: "uppercase" }}>
                {s.label.toUpperCase()}
              </text>
              <foreignObject x={x - 90} y={above ? y - 130 : y + 74} width="180" height="60">
                <p className="font-sans text-[11.5px] leading-snug text-center text-ink/60">{s.short}</p>
              </foreignObject>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export const Philosophy = () => (
  <section id="philosophy" className="relative bg-sand py-24 sm:py-36 overflow-hidden">
    <div aria-hidden className="pointer-events-none absolute -top-6 left-0 right-0 text-center">
      <span className="font-serif italic text-[22vw] leading-none text-ocean/[0.04] select-none">stages</span>
    </div>

    <div className="relative mx-auto max-w-[1300px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-y-12 gap-x-12">
        <div className="lg:col-span-6">
          <Reveal>
            <span className="font-sans text-xs uppercase tracking-[0.28em] text-cognac">Brian's Core Philosophy</span>
          </Reveal>
          <h2 className="mt-8 font-serif text-ocean tracking-[-0.02em] leading-[0.95] text-5xl sm:text-6xl lg:text-[5.4rem]">
            <MaskText lines={["You can't", "give birth", <span key="a" className="italic text-cognac">to adults.</span>]} />
          </h2>
        </div>

        <div className="lg:col-span-6 lg:pt-16 font-sans text-base sm:text-lg text-ink/75 leading-relaxed space-y-5 max-w-xl">
          <Reveal>
            <p>
              <span className="text-ocean font-medium">Brian Smith learned this the hard way.</span> UGG
              didn't arrive in America as the global brand the world knows today. It had to be born.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="font-serif italic text-xl sm:text-2xl text-ocean leading-snug">
              It crawled. It struggled. It grew.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p>
              It went through awkward stages, cash problems, competition, setbacks, partnerships,
              explosive opportunities, leadership changes and moments when the entire thing could
              have disappeared.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p>
              Over the years, Brian began to see business differently. A company develops much like
              a person does — and each stage requires something different from the founder.
            </p>
          </Reveal>
        </div>
      </div>

      <Curve />

      {/* Mobile fallback */}
      <Stagger data-testid="lifecycle-list" className="md:hidden mt-14 border-t border-[var(--line)]" amount={0.08}>
        {STAGES.map((s) => (
          <StaggerItem key={s.key} className="py-5 border-b border-[var(--line)] flex items-baseline gap-5">
            <span className="font-serif text-4xl text-ocean/15 leading-none">{s.n}</span>
            <div>
              <div className="font-sans text-[11px] uppercase tracking-[0.24em] text-cognac">{s.label}</div>
              <p className="mt-1 font-sans text-sm text-ink/65 leading-snug">{s.short}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>

      <div className="mt-20 sm:mt-24 grid lg:grid-cols-12 gap-x-12 gap-y-8 items-end">
        <Reveal className="lg:col-span-7">
          <blockquote className="font-serif text-ocean tracking-[-0.02em] leading-[1.05] text-3xl sm:text-4xl lg:text-[3.2rem]">
            The mistake is believing your business should already be
            <span className="italic text-cognac"> somewhere it isn't.</span>
          </blockquote>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-5">
          <p className="font-sans text-base sm:text-lg text-ink/75 leading-relaxed lg:border-l border-[var(--line)] lg:pl-6">
            This program helps you identify where you actually are — and build from there. And
            remember: even a mature company can have an infant-stage initiative.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);
